import { WebClientDetailFetcher } from '~commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '~commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { CERTIFIED_INDICATOR_TYPE } from '~src/app/quality/DataQualityType';
import { Messages } from '~src/codegen/Localize';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { IDetailFetcher } from '~src/ts/fetchers/IDetailFetcher';
import { IListFetcher } from '~src/ts/fetchers/IListFetcher';
import { ServerApiMapper } from '~src/ts/helpers/ServerModelHelper';
import { PublishedConnectionTableMapper } from '~src/ts/mappers/PublishedConnectionTableMapper';
import {
  IPublishedConnectionTableBase,
  IPublishedConnectionTableDetailInfo,
  IPublishedConnectionTableInfo,
} from '~src/ts/models/IPublishedConnectionTableInfo';
import { FieldValuesQuery } from '~src/ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IListResource } from '~src/ts/resources/Resource';
import { ResourceQuery } from '~src/ts/resources/ResourceQuery';
import { updateOrDeleteCertificationForItem } from '~src/ts/restApi/dataQuality/setDataQualityCertificationForItem';
import { AddTagsToPublishedConnectionTablesRequest } from '~src/ts/ServerApi/requests/AddTagsToPublishedConnectionTablesRequest';
import { GetFieldValuesRequest } from '~src/ts/ServerApi/requests/GetFieldValuesRequest';
import { GetPublishedConnectionTableActionsRequest } from '~src/ts/ServerApi/requests/GetPublishedConnectionTableActionsRequest';
import { GetPublishedConnectionTablesRequest } from '~src/ts/ServerApi/requests/GetPublishedConnectionTablesRequest';
import { RemoveTagsFromPublishedConnectionTablesRequest } from '~src/ts/ServerApi/requests/RemoveTagsFromPublishedConnectionTablesRequest';
import { RunExtractRefreshesOnPublishedConnectionRequest } from '~src/ts/ServerApi/requests/RunExtractRefreshesOnPublishedConnectionRequest';
import { IPublishedConnectionTableCertificationParams } from '~src/ts/ServerApi/requests/SetPublishedConnectionTableCertificationRequest';
import {
  SetPublishedConnectionTableDescriptionParams,
  SetPublishedConnectionTableDescriptionRequest
} from '~src/ts/ServerApi/requests/SetPublishedConnectionTableDescriptionRequest';
import { SetPublishedConnectionTableNameRequest } from '~src/ts/ServerApi/requests/SetPublishedConnectionTableNameRequest';
import { IFieldValue } from '~src/ts/ServerApi/types/IFieldValue';
import { IPublishedConnectionTableActions } from '~src/ts/ServerApi/types/IPublishedConnectionTableActions';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

class PublishedConnectionTablesResource implements IListResource<IPublishedConnectionTableInfo>, IDetailResource<IPublishedConnectionTableDetailInfo>, IActionResource<IPublishedConnectionTableActions> {

  private listFetcher: IListFetcher<IPublishedConnectionTableInfo> = new WebClientListResourceFetcher(GetPublishedConnectionTablesRequest, ServerApiMapper.publishedConnectionTables);
  private detailFetcher: IDetailFetcher<IPublishedConnectionTableDetailInfo> = new WebClientDetailFetcher('getPublishedConnectionTable', PublishedConnectionTableMapper.mapDetail)
  private fieldValuesFetcher: IListFetcher<IFieldValue> = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public all(): ResourceQuery<IPublishedConnectionTableInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<IPublishedConnectionTableDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'publishedConnectionTable');
  }

  public actions(id: string): Promise<IPublishedConnectionTableActions> {
    return WebHttpClient.sendRequest(new GetPublishedConnectionTableActionsRequest(id));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetPublishedConnectionTableNameRequest({
      id,
      name
    }));
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToPublishedConnectionTablesRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromPublishedConnectionTablesRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public setDescription = async (args: SetPublishedConnectionTableDescriptionParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetPublishedConnectionTableDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  }

  public runExtractRefreshNow(id: string, tableLuids: ReadonlyArray<string>, extractType: string, level: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new RunExtractRefreshesOnPublishedConnectionRequest({
        level,
        startingItems: [...tableLuids],
        targetId: id,
        type: extractType,
      }));
  }

  public setCertification(params: IPublishedConnectionTableCertificationParams & { luid?: string }): Promise<IResult> {
    return updateOrDeleteCertificationForItem({ ...params, contentType: 'publishedConnectionTable' });
  }

  public hasDataQualityWarning(contentItem: IPublishedConnectionTableBase): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators[0]
      && contentItem.dataQualityIndicators[0].isActive;
  }

  public hasCertification(contentItem: IPublishedConnectionTableBase): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators.some(dqi => dqi.isActive && dqi.type === CERTIFIED_INDICATOR_TYPE);
  }

  // Toast
  public toast(result: IResult, successMessage: string, failureMessage?: string): any {
    if (result.errors && result.errors.length > 0) {
      if (failureMessage) {
        this.popError(failureMessage);
      } else {
        result.errors.forEach((error: IError) => {
          this.popError(error.message);
        });
      }
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }

  public popError(errorMessage: string): any {
    ToasterService.getInstance().pop({
      type: ToastType.Error,
      text: errorMessage
    });
  }
}

export function isPublishedConnectionTableInfo(item: any): item is IPublishedConnectionTableInfo {
  return item &&
    item.contentType &&
    item.contentType === 'publishedConnectionTable';
}

export const PublishedConnectionTables = new PublishedConnectionTablesResource();
